import {
  AccessibleContent,
  Button,
  Flex,
  Heading,
  IconAddLine,
  InlineList,
  ScreenReaderContent,
  Tag,
  Text,
  TextInput,
} from "@instructure/ui";
import { t } from "i18next";
import { useContext, useState } from "react";
import { ApDataContext } from "../../../../views/Plan";
import { Card } from "../../../Card";
import { emailVerification } from "../utils";

const SectionReviewersCard = ({ section }) => {
  const { sectionIndex, setSections } = useContext(ApDataContext);
  const [approver, setApprover] = useState(section.approvers);
  const [invalidApprover, setInvalidApprover] = useState(false);
  const [reviewer, setReviewer] = useState("");
  const [invalidReviewer, setInvalidReviewer] = useState(false);
  const [duplicateReviewer, setDuplicateReviewer] = useState(false);

  const verifyApproverEmail = (email) => {
    if (emailVerification(email)) {
      setInvalidApprover(false);
    } else {
      setInvalidApprover(true);
    }
  };

  const handleUpdateApprover = () => {
    setSections((prev) =>
      prev.map((s, i) =>
        i === sectionIndex ? { ...s, approvers: approver } : s
      )
    );
  };

  const handleAddReviewer = () => {
    if (!emailVerification(reviewer)) {
      setInvalidReviewer(true);
      setDuplicateReviewer(false);
    } else if (section.reviewers.includes(reviewer)) {
      setDuplicateReviewer(true);
      setInvalidReviewer(false);
    } else {
      setInvalidReviewer(false);
      setDuplicateReviewer(false);
      setSections((prev) =>
        prev.map((s, i) =>
          i === sectionIndex
            ? { ...s, reviewers: [...s.reviewers, reviewer] }
            : s
        )
      );
      setReviewer("");
    }
  };

  const handleRemoveReviewer = (index) => {
    setSections((prev) =>
      prev.map((s, i) =>
        i === sectionIndex
          ? { ...s, reviewers: s.reviewers.filter((_, j) => j !== index) }
          : s
      )
    );
  };

  return (
    <>
      <Text weight="bold">{t("assessment.sections.sectionReviewers")}:</Text>
      <Card
        style={{ backgroundColor: "rgb(242, 248, 250)", marginTop: "0.75rem" }}
      >
        <Heading as="h3" level="h5" border="bottom" margin="0 0 small 0">
          {t("assessment.approver")}
        </Heading>
        <Flex>
          <Flex.Item>
            <TextInput
              renderLabel={
                <ScreenReaderContent>
                  {t("assessment.email")}
                </ScreenReaderContent>
              }
              display="inline-block"
              placeholder={t("assessment.email")}
              size="small"
              value={approver}
              onChange={(e) => setApprover(e.target.value)}
              onBlur={() => {
                if (approver !== "") {
                  verifyApproverEmail(approver);
                }
                handleUpdateApprover();
              }}
              messages={
                invalidApprover
                  ? [{ text: t("assessment.invalidEmail"), type: "error" }]
                  : []
              }
            />
          </Flex.Item>
        </Flex>
        <Heading as="h3" level="h5" border="bottom" margin="small 0">
          {t("assessment.reviewers")}
        </Heading>
        <Flex gap="small" alignItems="start" wrap="wrap">
          <Flex.Item>
            <TextInput
              id={`section-reviewers-input-${sectionIndex}`}
              renderLabel={
                <ScreenReaderContent>
                  {t("assessment.email")}
                </ScreenReaderContent>
              }
              placeholder={t("assessment.email")}
              size="small"
              value={reviewer}
              onChange={(e) => setReviewer(e.target.value)}
              messages={
                invalidReviewer
                  ? [{ text: t("assessment.invalidEmail"), type: "error" }]
                  : duplicateReviewer
                    ? [
                        {
                          text: t("assessment.reviewerAlreadyAdded"),
                          type: "error",
                        },
                      ]
                    : []
              }
            />
          </Flex.Item>
          <Flex.Item>
            <Button
              size="small"
              renderIcon={IconAddLine}
              onClick={handleAddReviewer}
            >
              {t("global.add")}
            </Button>
          </Flex.Item>
        </Flex>

        <InlineList>
          {section.reviewers.map((email, index) => (
            <InlineList.Item key={index} margin="small 0 0 0">
              <Tag
                text={
                  <AccessibleContent alt={t("assessment.deleteReviewer")}>
                    {email}
                  </AccessibleContent>
                }
                dismissible
                onClick={() => handleRemoveReviewer(index)}
              />
            </InlineList.Item>
          ))}
        </InlineList>
      </Card>
    </>
  );
};

export default SectionReviewersCard;
